<script setup lang="ts">
  import { inject, ref, watch } from 'vue';
  import { AlcButton } from '@alchemer/vue-component-library';
  import { useAuth0 } from '@auth0/auth0-vue';

  import LoginOption from './LoginOption/LoginOption.vue';

  import { fetchAuthUserProfile } from '@/api';
  import type { GetUserProfileResponseType } from '@/api/get-user-profile/get-user-profile.schema';
  import * as svgIcons from '@/assets/icon-svgs';
  import { EnvironmentalConfigKey } from '@/utils/environmental-config';
  import { LegacyDataCenterStorage } from '@/utils/local-storage';

  const { user: authenticatedUser, logout, getAccessTokenSilently } = useAuth0();
  const datacenterCount = ref(0);
  const datacenters = ref<Record<string, string[]>>({});
  const hasAppBeenClicked = ref(false);
  const environmentalConfig = inject(EnvironmentalConfigKey);
  const clickedApp = ref('');
  const numberOfApps = ref(0);
  const appListLoading = ref(true);

  const getDatacenterUrl = (
    app: string,
    datacenter: string
  ): string => {
    if (!environmentalConfig) return '';

    const email = authenticatedUser.value?.email || '';
    return environmentalConfig.getUrlForAppHandoff(app, datacenter, email);
  };

  const getDatacenterIcon = (datacenter: string): string => {
    const iconKey = environmentalConfig?.datacenters[datacenter]?.icon;
    return svgIcons[iconKey as keyof typeof svgIcons] || svgIcons['oewFlag'];
  };

  const getDatacenterLabel = (datacenter: string): string => {
    const label = environmentalConfig?.datacenters[datacenter]?.label;
    return label || '';
  };

  const processApplications = async() => {
    if (! environmentalConfig) {
      throw new Error('Environmental configuration is not available.');
    }

    if (! authenticatedUser.value) {
      throw new Error('User is not authenticated.');
    }

    try {
      let appsFromAppMetadata = authenticatedUser.value?.alchemerLoginOptions?.applications ?? {};

      const legacyDataCenterStorage = new LegacyDataCenterStorage();
      const allDatacenters = environmentalConfig.getDatacenterPriority();
      const legacyDatacenters = legacyDataCenterStorage.getItems(allDatacenters) || [];

      // add an empty array for each datacenter available in this environment
      const appsByDatacenter:Record<string, string[]> = {};
      allDatacenters.forEach((datacenter) => appsByDatacenter[datacenter] = []);

      const pushAppToDatacenter = (app:string, datacenter:string) => {
        // only push to a datacenter if it exists (aka is allowed in this environment)
        if (! appsByDatacenter[datacenter]) {
          return;
        }

        // don't duplicate apps
        if (appsByDatacenter[datacenter].includes(app)) {
          return;
        }

        appsByDatacenter[datacenter].push(app);
      }

      // Add legacy survey to datacenters (pulled from AuthMethod calls)
      legacyDatacenters.forEach((datacenter) => pushAppToDatacenter('legacy_survey', datacenter));

      // Add applications from Auth0 App Metadata (should only be used for Digital)
      const allowedMetadataApps = environmentalConfig.getAllowedMetadataApps();
      allowedMetadataApps.forEach((app:string) => {
        if (appsFromAppMetadata[app]) {
          const datacenters = appsFromAppMetadata[app]?.datacenters;
          datacenters.forEach((datacenter:string) => pushAppToDatacenter(app, datacenter));
        }
      });

      let userProfile = {} as GetUserProfileResponseType;

      try {
        // Add applications from All Account (will be used for all applications in the future)
        userProfile = await fetchAuthUserProfile(environmentalConfig, getAccessTokenSilently);
      } catch (error) {
        appListLoading.value = false;
        console.error('Error fetching all-account user profile:', error);
      }

      const appUsers = userProfile?.appUsers || [];
      appUsers.forEach((appUser) => pushAppToDatacenter(appUser.app, appUser.dataCenter));

      // clean up empty datacenters and count applications
      allDatacenters.forEach((datacenter) => {
        if (! appsByDatacenter[datacenter]) return;

        if (appsByDatacenter[datacenter].length === 0) {
          delete appsByDatacenter[datacenter];
          return;
        }

        numberOfApps.value += appsByDatacenter[datacenter].length;
      });

      appListLoading.value = false;
      datacenterCount.value = Object.keys(allDatacenters).length;

      if (numberOfApps.value === 0) {
        throw new Error('No applications found for the authenticated user.');
      }

      if (numberOfApps.value === 1) {
        const singleDatacenterKey = Object.keys(appsByDatacenter)[0];
        const singleAppKey = appsByDatacenter[singleDatacenterKey][0];
        const appUrl = getDatacenterUrl(singleAppKey, singleDatacenterKey);

        if (appUrl) {
          window.location.href = appUrl;
        } else {
          throw new Error('URL not found for the application.');
        }
      }

      datacenters.value = appsByDatacenter;
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  watch(
    () => [authenticatedUser.value],
    () => {
      processApplications();
    },
    {
      immediate: true,
    },
  );

  const handleLogoutClick = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleAppClick = (app: string) => {
    hasAppBeenClicked.value = !hasAppBeenClicked.value;
    clickedApp.value = app;

    setTimeout(() => {
      hasAppBeenClicked.value = false;
    }, 3000);
  };

  const appIsLoading = (app: string) => {
    return hasAppBeenClicked.value && clickedApp.value === app;
  };
</script>
<template>
  <div class="login-options">
    <div class="login-options__header">Your Apps</div>
    <section v-if="environmentalConfig && appListLoading">
      <div class="login-options__applications">
        Loading applications...
      </div>
    </section>
    <section v-else-if="environmentalConfig && !appListLoading && numberOfApps < 1">
      <div class="login-options__applications">
        No applications were found for your account.
      </div>
    </section>
    <section v-else-if="environmentalConfig && !appListLoading && numberOfApps > 0">
      <div class="login-options__applications">
        <div
          v-for="(datacenter, key) in datacenters"
          :key="key"
          class="login-options__application-datacenter"
        >
          <div
            v-if="datacenterCount > 1"
            class="login-options__application-datacenter-label"
          >
            <div v-html="getDatacenterIcon(key)"></div>
            <div>{{ getDatacenterLabel(key) }}</div>
          </div>
          <div class="login-options__application">
            <LoginOption
              v-for="app in datacenter"
              :key="app"
              :icon="svgIcons[`${app}Icon`]"
              :label="environmentalConfig.getAppLabel(app)"
              :url="getDatacenterUrl(app, key)"
              :disabled="hasAppBeenClicked"
              :loading="appIsLoading(app)"
              @click="handleAppClick(app)"
            />
          </div>
        </div>
      </div>
    </section>
    <section v-else>
      <div class="login-options__applications">
        Looks like something went wrong. Please reload the page and try again.
      </div>
    </section>
    <div class="login-options__footer">
      <AlcButton
        variant="secondary"
        label="Logout"
        :stretch="true"
        @alc-btn-click="handleLogoutClick"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .login-options {
    color: var(--alc-base-800);
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__header {
      color: var(--alc-base-800);
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    }

    &__applications {
      display: flex;
      flex-direction: column;
      gap: 16px;
      color: var(--alc-base-800);
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      height: 280px;
      overflow: auto;
    }

    &__scroll-indicator {
      background-color: var(--alc-base-100);
      text-align: center;
      transform: translateY(-20px);
    }

    &__application-datacenter-label {
      display: flex;
      gap: 4px;
      margin-bottom: 4px;
    }

    &__application {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      gap: 8px;
    }
    @media (max-width: 768px) {
      &__applications {
        height: auto;
      }
      &__application {
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      }
      &__scroll-indicator {
        display: none;
      }
    }
  }
</style>
